import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, inject } from '@angular/core';
import { PosthogService } from '../../../../services/analytics/posthog.service';
import { environment } from '../../../../../environments/environment';
import { ThemeService } from '../../../../services/theme/theme.service';
import { getTurnstileApi, TurnstileScriptService } from '../../../onboarding-v2/service/turnstile-script/turnstile-script.service';
import { ValueFirstOnboardingService } from '../../services/value-first-onboarding.service';
import { BRAND_TRIAL_LINE, brandInitial } from '../../services/value-first-onboarding.types';

@Component({
  selector: 'vf-email-step',
  templateUrl: './email-step.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VfEmailStep implements OnInit, AfterViewInit, OnDestroy {
  readonly onboarding = inject(ValueFirstOnboardingService);
  private readonly posthog = inject(PosthogService);
  private readonly turnstileScript = inject(TurnstileScriptService);
  private readonly theme = inject(ThemeService);

  @ViewChild('turnstileContainer') turnstileContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('honeypotInput') honeypotInput?: ElementRef<HTMLInputElement>;

  private turnstileWidgetId: string | null = null;

  readonly priceLine = computed(() => {
    const currency = this.onboarding.displayCurrency();
    return currency ? BRAND_TRIAL_LINE[currency] : '14-day free trial. Cancel anytime.';
  });

  initial(): string {
    return brandInitial(this.onboarding.brandName());
  }

  ngOnInit(): void {
    this.posthog.posthog.capture('signup_page_viewed', {
      flow: 'value_first',
      step: 'email',
    });
  }

  ngAfterViewInit(): void {
    void this.initTurnstile();
  }

  ngOnDestroy(): void {
    if (this.turnstileWidgetId) {
      getTurnstileApi()?.remove(this.turnstileWidgetId);
      this.turnstileWidgetId = null;
    }
  }

  onEmail(event: Event): void {
    this.onboarding.setEmail((event.target as HTMLInputElement).value);
  }

  submit(event: Event): void {
    event.preventDefault();
    void this.onboarding.sendCode(this.honeypotInput?.nativeElement.value ?? '');
  }

  google(): void {
    void this.onboarding.continueWithGoogle();
  }

  private async initTurnstile(): Promise<void> {
    try {
      await this.turnstileScript.load();
    } catch {
      return;
    }
    const turnstile = getTurnstileApi();
    if (!turnstile || !this.turnstileContainer?.nativeElement) {
      return;
    }
    this.turnstileWidgetId = turnstile.render(this.turnstileContainer.nativeElement, {
      sitekey: environment.cloudflare.turnstileSiteKey,
      callback: (token: string) => this.onboarding.setTurnstileToken(token),
      'expired-callback': () => this.onboarding.setTurnstileToken(null),
      'error-callback': () => this.onboarding.setTurnstileToken(null),
      theme: this.theme.isDarkMode() ? 'dark' : 'light',
      appearance: 'interaction-only',
    });
  }
}
